import { AddressComponent, Result } from "./PlaceDetail";
import StringUtil from './../../utils/stringUtils';
import { Lang } from "../../states/query";
import { GeoLocation } from "../../utils/geoLocation";
export interface PlaceLog {
  prefecture: string;
  city: string;
  address: string;
  place: string;
  category: string;
}

export interface PlaceData {
  /** 緯度。 */
  lat: string;
  /** 経度。 */
  lng: string;
  /** 都道府県。 */
  prefecture: string;
  /** 市区町村。 */
  city: string;
  /** その他の住所。 */
  address: string;
  /** 地点名。 */
  name: string;
  /** 地点のカテゴリ。 */
  category: string;
  /** 住所情報 */
  formattedAddress: string;
}

/** Google Places APIから取得した地点を扱うクラス。（検索ログ用の情報を格納する。） */
export default class Place implements PlaceData {
  /** 緯度。 */
  lat: string;
  /** 経度。 */
  lng: string;
  /** 都道府県。 */
  prefecture: string;
  /** 市区町村。 */
  city: string;
  /** その他の住所。 */
  address: string;
  /** 地点名。 */
  name: string;
  /** 地点のカテゴリ。 */
  category: string;
  /** 住所情報 */
  formattedAddress: string;

  // 定数。
  /** 都道府県として判別するキー。 */
  prefectureKey = 'administrative_area_level_1';
  /** 市区町村として判別するキー（その1）。 */
  administrativeAreaLv2Key = 'administrative_area_level_2';
  /** 市区町村として判別するキー（その2）。 */
  cityKey1 = 'locality';
  /** 市区町村として判別するキー（その3）。 */
  cityKey2 = 'sublocality_level_1';
  /** その他の住所として判別するキー。 */
  premiseKey = 'premise';
  /** 郵便番号として判別するキー。 */
  postalCode = 'postal_code';
  /** 国名として判別するキー。 */
  country = 'country';
  /** 全角マイナス。 */
  fillwidthMinus = '−';

  /** リロード時にも情報を復元できるように格納するSession Storageのキー。 */
  static readonly sessionStorageKeys = {
    /** 地点の名称を格納するキー。 */
    latlng: 'ana-airport-access-navi_latlng',
  };

  /**
   * コンストラクタ。
   * @param response Place Details（https://maps.googleapis.com/maps/api/place/details/）で取得したレスポンス。
   */
  constructor(result: Result);
  constructor(result: Place);
  constructor(result: PlaceData);
  constructor(result: any) {
    // geometry 要素があればPlaceData Typeと判定する
    if(result.geometry) {
      this.lat = result.geometry.location.lat.toString();
      this.lng = result.geometry.location.lng.toString();
      // 都道府県・市区町村は別メソッドで設定するため、一旦空文字をセットする。
      this.prefecture = '';
      this.city = '';
      // 住所情報を取得する
      this.formattedAddress = result.formatted_address;
      // 都道府県・市区町村を設定する。
      this.setFromAddressComponents(result.address_components);
      // その他住所をaddress_components要素から設定する。
      this.address = this.setAddress(result.address_components);
      // 地点名称はname要素から取得する。
      this.name = result.name;
      // スラッシュ区切りでカテゴリ（type）の要素を1つの文字列にする。
      this.category = result.types.join('/');
    } else {
      this.lat = result.lat;
      this.lng = result.lng;
      this.prefecture = result.prefecture;
      this.city = result.city;
      this.address = result.address;
      this.name = result.name;
      this.category = result.category;
      this.formattedAddress = result.formattedAddress;
    }
  }

  /** places/geocode で地点を取得した場合には地点名が取れないので後入れをする */
  setPlaceName(name: string) {
    if(!this.name || this.name === '') {
      this.name = name;
    }
  }

  /**
   * 住所（都道府県・市区町村・字（＋番地など））の文字列を取得する。
   * @param lang 言語設定。未指定の場合は日本語版となる。
   */
  getFullAddress = (lang: Lang = Lang.ja) => {
    // 日本語版の場合は「都道府県」「市区町村」「字（＋番地など）」の順の文字列を返す。
    if(lang === Lang.ja) {
      return `${this.prefecture}${this.city}${this.address}`;
    }
    // 英語版の場合は「字（＋番地など）」「市区町村」「都道府県」の順の文字列を返す。
    else {
      return `${this.address}, ${this.city}, ${this.prefecture}`;
    }
  }

  /**
   * 市区町村までの住所（都道府県・市区町村）の文字列を取得する。
   * （空港送迎タクシーの予約時のエリア判別で利用する。2021/07/12現在。）
   */
  getAddressToMunicipality = () => {
    // 「都道府県」「市区町村」を組み合わせた文字列を返す。
    return `${this.prefecture}${this.city}`;
  }

  /**
   * address_component要素から都道府県・市区町村を設定する。
   * @param addressComponent Place Detailsで取得したレスポンスのaddress_component要素。
   */
  setFromAddressComponents = (addressComponent: AddressComponent[]) => {
    let prefecture = '';
    const cityList: AddressComponent[] = [];
    // 日本の住所の記法とは逆順でデータが返却されるため、配列の順序を逆転させた上でループさせる。
    addressComponent.reverse().forEach((c) => {
      const types = c.types;
      const longName = c.long_name;
      // 名古屋駅などはadministrative_area_level1と2が同じ愛知県になっているため、そのようなケースは捨てる。
      if (types.includes(this.administrativeAreaLv2Key) && prefecture === longName) { return; }
      // 種別が郵便番号または国名の場合はスキップする。
      if (types.includes(this.postalCode) || types.includes(this.country)) {
        return;
      // 種別に都道府県を表すキーを含んでいる場合は都道府県の名称として採用する。
      } else if (types.includes(this.prefectureKey)) {
        prefecture = longName;
      // 種別に市区町村を表すキーを含んでいる場合は市区町村の名称として採用する。
      } else if (types.includes(this.cityKey1) || types.includes(this.cityKey2) || types.includes(this.administrativeAreaLv2Key)) {
        cityList.push(c);
      }
    })
    // 市区町村を求める
    this.city = cityList.map((c)=>{
      // 該当地点がフルアドレス中で何文字目に来るかを求める
      c.index = this.formattedAddress.indexOf(c.long_name);
      return c;
    })
    // フルアドレスにマッチしない地名は除外する
    .filter((x)=>{
      return x.index !== -1;
    })
    // フルアドレスに出てくる順に並び替える
    .sort((a, b)=>{
      if(a.index === undefined || b.index === undefined) {
        return 0;
      }
      else {
        return a.index > b.index? 1: -1;
      }
    })
    // フルアドレスに出てくる順に構築する
    .reduce((x, a)=>{
      return x + a.long_name;
    }, "");
    /**
     * TODO:「奈良県宇陀郡曽爾村」を選択した際に「奈良県曽爾村宇陀郡」になる場合があるので修正処理を入れるが、
     * デグレが発生する可能性があるので一旦コメントアウトしている（2022/03/04）
     **/
    // this.city = this.fixCity()
    this.prefecture = prefecture;
  }

  /**
   * Google APIのデータからその他住所を設定する。
   * @param addressComponents Google Places Details APIの住所データの配列。
   */
  setAddress = (addressComponents: AddressComponent[]) => {
    let address = '';
    /** 
     * type要素に「sublocality_level_2」 ~ 「sublocality_level_5」が設定されている要素を格納する配列。
     * https://developers.google.com/places/supported_types
     * */
    const sublocalityElements: AddressComponent[] = [];
    /** 都道府県より先の住所として扱う住所要素のキーを格納する配列。 */
    const sublocalityKeys: string[] = [];
    /** 都道府県より先の住所として扱う住所要素のキーの共通する文字列。 */
    const subLocalityKey = 'sublocality_level_';
    // 住所のコンポーネントをループする。
    addressComponents.forEach((c) => {
      // type要素の全項目を評価する。
      c.types.forEach((type) => {
        // type要素の配列に「sublocality_level_」を含む文字列を含む、かつ、「sublocality_level_1」（都道府県名称）ではない場合、そのtype要素の値を配列に格納する。
        if (type.includes(subLocalityKey) && type !== 'sublocality_level_1') { sublocalityKeys.push(type); }
      })
    })
    // ソートした「sublocality_level_x」のキーをループする。
    // （末尾の「x」部分が数字であるため、標準メソッドのソートで並び替えることで順番どおりにループさせる。）
    sublocalityKeys.sort().forEach((key) => {
      // あらかじめ取得したキーを持つ住所コンポーネントを取得する。
      const targetComponent = addressComponents.find((c) => { return c.types.includes(key); })
      // 住所コンポーネントが取得できた場合のみ、配列に格納する。
      if (targetComponent) { sublocalityElements.push(targetComponent); }
    })
    // 上記のwhile文で「sublocality_level_x」の数字順に取得したsublocality要素をループさせる。
    sublocalityElements.forEach((s) => {
      const longName = s.long_name;
      // 参照している要素が番地の形式（数字のみ、または全角マイナスつなぎの数字）、かつ、現在取得しているその他住所の末尾が数字である場合。
      if (StringUtil.isHouseNumber(longName) && StringUtil.endsWithNumbers(address)) {
        // 全角マイナスを追加する。
        address += this.fillwidthMinus;
      }
      address += longName;
    })
    // sublocality以下の住所を取得する。
    addressComponents.forEach((c) => {
      const types = c.types;
      if (types.includes(this.premiseKey)) {
        const longName = c.long_name;
        // 番地の形式（数字のみ、または全角マイナスつなぎの数字）である場合。
        if (StringUtil.isHouseNumber(longName)) {
          // 現在取得しているその他住所の末尾が数字で終わっている場合は全角マイナスを追加する。
          if (StringUtil.endsWithNumbers(address)) { address += this.fillwidthMinus; }
          address += longName;
        }
      }
    })
    // ここまでの処理で「address」が空の場合には「establishment」属性の要素を追加する
    // AM-796「山形県酒田市　豊川」対応のための処理
    if(address === '') {
      addressComponents.forEach((c) => {
        if(c.types.includes('establishment')) {
          const longName = c.long_name;
          address += longName;
        }
      })
    }
    return address;
  }

  /** カンマ区切りの緯度経度（駅すぱあとの経路探索で利用する形式）を取得する。 */
  getCoordinate = () => {
    return this.lat + ',' + this.lng;
  }

  /** ログ記録時に利用するオブジェクトに変換する。 */
  toLog = (): PlaceLog => {
    return {
      prefecture: this.prefecture,
      city: this.city,
      address: this.address,
      place: this.name,
      category: this.category,
    }
  }

  /** 地点名の座標をGeoLocation形に変換する */
  getLocation = (): GeoLocation => {
    return {
      lat: Number(this.lat),
      lng: Number(this.lng)
    }
  }

  /**
   * XXX村XX郡の並びになっている場合には並びを戻す
   * 奈良県宇陀郡曽爾村を選択した場合に住所がおかしくなる場合があるので、XX郡が含まれる際に並びが正しいかチェックする
   * TODO: 1ケースしか試していないので他の市区町村のパターンでも正確に直るかを検証したい
   **/
  fixCity = () => {
    const matchPattern = /((?:旭川|伊達|石狩|盛岡|奥州|田村|南相馬|那須塩原|東村山|武蔵村山|羽村|十日町|上越|富山|野々市|大町|蒲郡|四日市|姫路|大和郡山|廿日市|下松|岩国|田川|大村)市|.+?郡(?:玉村|大町|.+?)[町村]|.+?市.+?区|.+?[市区町村])(.+)/
    const matchResult = this.city.match(matchPattern);
    // XX村の後にXXX郡が来ている場合にはfalseとする
    if(matchResult && matchResult[2] && matchResult[2].match(/.+?郡$/)) {
      return `${matchResult[2]}${matchResult[1]}`;
    }
    return this.city;
  }
}
